import { v4 as uuid } from "uuid";

import Api from "@/api/Api";
import { API_ROUTES, requestTokens } from "@/settings/apiRoutes";
import { Advertiser } from "@/store/modules/planCreate";
import { AdvertiserDetail, AdvertisersCatalog, AdvertisersListInput, AdvertisersListOutput } from "@/types/advertisers";
import { AdvertiserExtendedListEntity, AdvertiserListEntity } from "@/types/plans";

const emptyAdvertisersListOutput: AdvertisersListOutput = {
  page: 1,
  page_size: 0,
  total: 0,
  next_page: null,
  data: [],
};

const emptyAdvertisersCatalog: AdvertisersCatalog = {
  client_status: [],
  industries: [],
  location_dimensions: [],
  dma_count_groups: [],
};

export default class AdvertisersApi {
  async getAdvertisersList(input: AdvertisersListInput): Promise<AdvertisersListOutput> {
    return await Api.request<AdvertisersListOutput>({
      path: `${API_ROUTES.ADVERTISER_CONSOLE.PATH}/advertisers/list`,
      method: "post",
      body: input,
      token: requestTokens.apiGetAdvertisersList,
      defaultResult: { ...emptyAdvertisersListOutput },
    });
  }

  async getAdvertiserByKey(advertiserKey: string): Promise<AdvertiserDetail | null> {
    return await Api.request<AdvertiserDetail | null>({
      path: `${API_ROUTES.ADVERTISER_CONSOLE.PATH}/advertisers/${encodeURIComponent(advertiserKey)}`,
      method: "get",
      token: requestTokens.apiGetAdvertiserDetail,
      defaultResult: null,
    });
  }

  async getAdvertisersCatalog(): Promise<AdvertisersCatalog> {
    return await Api.request<AdvertisersCatalog>({
      path: `${API_ROUTES.ADVERTISER_CONSOLE.PATH}/catalog`,
      method: "get",
      token: requestTokens.apiGetAdvertisersCatalog,
      defaultResult: { ...emptyAdvertisersCatalog },
    });
  }

  async getAllAdvertisers(full = false): Promise<AdvertiserListEntity[] | AdvertiserExtendedListEntity[]> {
    const params = full ? { full: "" } : {};
    return await Api.request<AdvertiserListEntity[] | AdvertiserExtendedListEntity[]>({
      path: `${API_ROUTES.ADVERTISERS.PATH}`,
      method: "get",
      params: params,
      token: requestTokens.apiGetAdvertisers,
      defaultResult: [],
    });
  }

  async getAdvertisersByAgency(agencyName: string): Promise<AdvertiserListEntity[] | AdvertiserExtendedListEntity[]> {
    return await Api.request<AdvertiserListEntity[] | AdvertiserExtendedListEntity[]>({
      path: `${API_ROUTES.ADVERTISERS.PATH}`,
      method: "get",
      params: { agency_name: agencyName },
      token: requestTokens.apiGetAdvertisers,
      defaultResult: [],
    });
  }

  async getAllAdvertiserByID(id: string): Promise<Advertiser> {
    return await Api.request<Advertiser>({
      path: `${API_ROUTES.ADVERTISERS.PATH}/${id}`,
      method: "get",
      token: requestTokens.apiGetAdvertiser,
      defaultResult: {
        id: "",
        name: "",
      },
    });
  }

  async updateAdvertiserById(id: string, payload: Advertiser): Promise<Advertiser> {
    return await Api.request<Advertiser>({
      path: `${API_ROUTES.ADVERTISERS.PATH}/${id}`,
      method: "put",
      token: requestTokens.apiUpdateAdvertiser,
      body: payload,
      defaultResult: {
        id: "",
        name: "",
      },
    });
  }

  async createAdvertiser(payload: Omit<Advertiser, "id">): Promise<Advertiser> {
    const payloadWithId = {
      id: uuid(),
      ...payload,
    };

    return await Api.request<Advertiser>({
      path: API_ROUTES.ADVERTISERS.PATH,
      method: "post",
      token: requestTokens.apiCreateAdvertiser,
      body: payloadWithId,
      defaultResult: {
        id: "",
        name: "",
      },
    });
  }
}
